import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { MapBrazil } from '../../../components/MapBrasil'
import { TopTenChart } from './TopChart'
import api from '../../../services/axios'
import { FamilyRevenueChart } from './TopChart/FamilyRevenyeChart'
import { Skeleton } from '../../../components/Skeletron'
import { useGeralFilter } from '../../../context/FiltersContext/PeriodoFilter'
import { MarketMarketDetailsModal } from '../../../components/MarketDetailsModal'
import { FamilyCoverageChart } from './TopChart/FamilyCoverageChart'
import { RiFullscreenLine } from 'react-icons/ri'
import { TiArrowMinimise } from 'react-icons/ti'
import { normalizeDateForComparison } from '../../../constantes'
import { useAuth } from '../../../context/AuthContext'

const monthNames = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]

interface DashboardData {
  kpis: {
    totalSellout: number
    activeClients: number
    coberturaItens: number
    clientsCount: number
    ufCount: number
    potencialPdvs?: number | null
    atingimentoPotencialPct?: number | null
    gapPdvs?: number | null
    ufReferencia?: string | null
    compareSellout?: number | null
    compareActiveClients?: number | null
    compareCoberturaItens?: number | null
    growthSellout?: number | null
    growthActiveClients?: number | null
    growthCoberturaItens?: number | null
  }
  mapData: Record<string, number>
  compareMapData?: Record<string, number> | null
  top10: { name: string; total: number }[]
  categoryMix: { name: string; total: number }[]
  distribuidores: {
    id: number
    name: string
    total: number
    coberturaClientes: number
    coberturaItens: number
  }[]
}

export default function ClientSumariCxScreen() {
  const {
    distributorId,
    setDistributor,
    distributorIds,
    toggleDistributor,
    clearDistributors,
    startDate,
    endDate,
    categoria,
    marca,
    setMarca,
    year,
    compareYear,
    month,
    compareMonth,
    endMonth,
    visao,
    semana,
    trimestre,
    operacao,
    gestor,
  } = useGeralFilter()

  const { user } = useAuth()
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedUF, setSelectedUF] = useState<string | null>(null)
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)
  const [activeChartTab, setActiveChartTab] = useState<'revenue' | 'coverage'>('revenue')
  const [isMinimized, setIsMinimized] = useState(true)
  const [userInitialized, setUserInitialized] = useState(false)

  useEffect(() => {
    const checkUserAccess = async () => {
      try {
        const vincMarca = user?.vinc_marca

        if (vincMarca) {
          if (marca !== vincMarca) {
            setMarca(vincMarca)
          } else {
            setUserInitialized(true)
          }
        } else {
          setUserInitialized(true)
        }
      } catch (error) {
        console.error('Erro ao verificar permissões do usuário:', error)
        setUserInitialized(true)
      }
    }

    checkUserAccess()
  }, [marca, setMarca])

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      const selectedMonth = visao === 'trimestre' ? -1 : month
      const selectedEndMonth = visao === 'trimestre' ? -1 : (endMonth === -1 ? month : endMonth)
      const selectedSemana = visao === 'semanas' ? semana : null

      const { data } = await api.get('/cx/sumari', {
        params: {
          distributorId: distributorIds.length > 0 ? distributorIds.join(',') : distributorId,
          distributorIds: distributorIds.length > 0 ? distributorIds.join(',') : null,
          operacao,
          gestor,
          categoria,
          marca,
          year,
          compareYear,
          month: selectedMonth,
          endMonth: selectedEndMonth,
          compareMonth,
          visao,
          semana: selectedSemana,
          trimestre: visao === 'trimestre' ? trimestre : null,
          startDate: startDate ? normalizeDateForComparison(startDate) : null,
          endDate: endDate ? normalizeDateForComparison(endDate) : null,
        },
      })

      setDashboardData(data)
      setLastUpdate(new Date())
    } catch (error) {
      console.error('Erro ao carregar os dados consolidados:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!userInitialized) return

    loadDashboardData()
  }, [
    userInitialized,
    distributorId,
    distributorIds,
    startDate,
    endDate,
    categoria,
    marca,
    gestor,
    operacao,
    year,
    compareYear,
    month,
    compareMonth,
    endMonth,
    visao,
    semana,
    trimestre,
  ])

  if (!userInitialized || loading || !dashboardData) return <Skeleton />

  const { kpis, mapData, top10, categoryMix, distribuidores } = dashboardData

  return (
    <Container>
      <HeaderSection>
        <TitleGroup>
          <h2>Análise de Mercado (Volume CX)</h2>
          <p>Visão estratégica e performance de sellout nacional</p>
          <p style={{ fontSize: '14px', color: '#a6c10b', marginTop: '5px' }}>
            Período:{' '}
            <strong>
              {startDate && endDate
                ? `${startDate.toLocaleDateString()} até ${endDate.toLocaleDateString()}`
                : visao === 'ano'
                  ? month === endMonth
                    ? `${monthNames[month]} / ${year} vs ${compareYear}`
                    : `${monthNames[month]} a ${monthNames[endMonth]} / ${year} vs ${compareYear}`
                  : visao === 'mes'
                    ? `${monthNames[month]}/${year} vs ${monthNames[compareMonth]}/${compareYear}`
                    : visao === 'trimestre'
                      ? `Trimestre ${trimestre === 'todos' ? 'Geral (Q1 - Q4)' : trimestre}: ${year} vs ${compareYear}`
                      : visao === 'semanas'
                        ? month === endMonth
                          ? `${monthNames[month]} / ${year} (${semana === 'todas' ? 'S1 a S4' : semana}) vs ${compareYear}`
                          : `${monthNames[month]} a ${monthNames[endMonth]} / ${year} (${semana === 'todas' ? 'S1 a S4' : semana}) vs ${compareYear}`
                        : `${monthNames[month]} / ${year}`}
            </strong>
          </p>
        </TitleGroup>
        <Badge>
          Última atualização às{' '}
          {lastUpdate?.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
        </Badge>
      </HeaderSection>

      <DashboardGrid>
        <LeftPanel>
          <KpiRow>
            <KpiCard style={{ borderLeft: '4px solid #f39c12' }}>
              <label>Positivação / Potencial</label>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <strong>{Number(kpis.activeClients || 0).toLocaleString('pt-BR')}</strong>
                <strong style={{ color: '#64748b', fontSize: '20px' }} title="Potencial Demográfico IBGE (1 PDV / 1.000 hab.)">
                  {kpis.potencialPdvs ? Number(kpis.potencialPdvs).toLocaleString('pt-BR') : '-'}
                </strong>
              </div>

              {kpis.potencialPdvs ? (
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    marginTop: '4px',
                    display: 'block',
                    color: (kpis.atingimentoPotencialPct || 0) >= 50 ? '#a6c10b' : '#f39c12',
                  }}
                >
                  Gap: {kpis.atingimentoPotencialPct || 0}% ({Number(kpis.gapPdvs || 0).toLocaleString('pt-BR')} PDVs)
                </span>
              ) : null}

              {kpis.compareActiveClients !== null && kpis.compareActiveClients !== undefined && (
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 'bold',
                    marginTop: '4px',
                    display: 'block',
                    color: (kpis.growthActiveClients || 0) >= 0 ? '#a6c10b' : '#ff4d4f',
                  }}
                >
                  {(kpis.growthActiveClients || 0) >= 0 ? '▲ +' : '▼ '}
                  {kpis.growthActiveClients}% vs {compareYear} ({Number(kpis.compareActiveClients).toLocaleString('pt-BR')})
                </span>
              )}
            </KpiCard>
            <KpiCard style={{ borderLeft: '4px solid #a6c10b' }}>
              <label>Presença Nacional</label>
              <strong>{kpis.ufCount} UFs</strong>
              {kpis.compareCoberturaItens !== null && kpis.compareCoberturaItens !== undefined && (
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 'bold',
                    marginTop: '4px',
                    display: 'block',
                    color: (kpis.growthCoberturaItens || 0) >= 0 ? '#a6c10b' : '#ff4d4f',
                  }}
                >
                  {(kpis.growthCoberturaItens || 0) >= 0 ? '▲ +' : '▼ '}
                  {kpis.growthCoberturaItens}% itens ({kpis.coberturaItens} itens)
                </span>
              )}
            </KpiCard>
            <KpiCard style={{ borderLeft: '4px solid #1890ff' }}>
              <label>Volume Total (CX)</label>
              <strong className='price'>
                {Math.round(kpis.totalSellout).toLocaleString('pt-BR')} CX
              </strong>
              {kpis.compareSellout !== null && kpis.compareSellout !== undefined && (
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 'bold',
                    marginTop: '4px',
                    display: 'block',
                    color: (kpis.growthSellout || 0) >= 0 ? '#a6c10b' : '#ff4d4f',
                  }}
                >
                  {(kpis.growthSellout || 0) >= 0 ? '▲ +' : '▼ '}
                  {kpis.growthSellout}% vs {compareYear} ({Math.round(kpis.compareSellout).toLocaleString('pt-BR')} CX)
                </span>
              )}
            </KpiCard>
          </KpiRow>

          <MapWrapper>
            <MapBrazil
              data={mapData}
              onStateClick={(uf: string) => {
                setSelectedUF(uf)
              }}
              isVolumeMode={true}
              unitLabel='PDVs Ativos (CX)'
              selectedUf={selectedUF}
              filters={{
                distributorId: distributorIds.length > 0 ? distributorIds.join(',') : distributorId,
                distributorIds: distributorIds.length > 0 ? distributorIds.join(',') : null,
                startDate: startDate ? normalizeDateForComparison(startDate) : null,
                endDate: endDate ? normalizeDateForComparison(endDate) : null,
                categoria,
                marca,
                year,
                compareYear,
                month: visao === 'trimestre' ? -1 : month,
                endMonth: visao === 'trimestre' ? -1 : (endMonth === -1 ? month : endMonth),
                compareMonth,
                semana: visao === 'semanas' ? semana : null,
                trimestre: visao === 'trimestre' ? trimestre : null,
                gestor,
                operacao,
                visao,
              }}
            />
          </MapWrapper>
        </LeftPanel>
        <RightPanel>
          <GlassCard>
            <h3>
              <span className='dot' /> Top 10 Performance (CX)
            </h3>
            <TopTenChart data={top10} isVolumeMode={true} />
          </GlassCard>

          <GlassCard>
            <CardHeader>
              <h3>
                <span
                  className='dot'
                  style={{
                    background: activeChartTab === 'revenue' ? '#a6c10b' : '#1890ff',
                    boxShadow: `0 0 10px ${activeChartTab === 'revenue' ? '#a6c10b' : '#1890ff'}`,
                  }}
                />
                {activeChartTab === 'revenue'
                  ? 'Mix por Categoria (R$)'
                  : 'Cobertura por Categoria (PDVs)'}
              </h3>
              <TabGroup>
                <TabButton
                  $active={activeChartTab === 'revenue'}
                  onClick={() => setActiveChartTab('revenue')}
                >
                  Faturamento
                </TabButton>
                <TabButton
                  $active={activeChartTab === 'coverage'}
                  onClick={() => setActiveChartTab('coverage')}
                >
                  Cobertura
                </TabButton>
              </TabGroup>
            </CardHeader>
            {activeChartTab === 'revenue' ? (
              <FamilyRevenueChart data={categoryMix} isVolumeMode={true} />
            ) : (
              <FamilyCoverageChart data={categoryMix} />
            )}
          </GlassCard>
        </RightPanel>
      </DashboardGrid>

      {selectedUF && (
        <MarketMarketDetailsModal
          uf={selectedUF}
          filters={{
            distributorId: distributorIds.length > 0 ? distributorIds.join(',') : distributorId,
            distributorIds: distributorIds.length > 0 ? distributorIds.join(',') : null,
            startDate: startDate ? normalizeDateForComparison(startDate) : null,
            endDate: endDate ? normalizeDateForComparison(endDate) : null,
            categoria,
            marca,
            year,
            compareYear,
            month: visao === 'ano' ? -1 : month,
            endMonth: visao === 'semanas' ? endMonth : visao === 'ano' ? -1 : month,
            semana: visao === 'semanas' ? semana : null,
            gestor,
            operacao,
          }}
          close={() => setSelectedUF(null)}
          isVolumeMode={true}
        />
      )}
    </Container>
  )
}

const Container = styled.div`
  padding: 20px;
  margin-bottom: 100px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  min-height: 100vh;
  font-family: 'Montserrat', sans-serif;

  @media (max-width: 768px) {
    padding: 15px 10px;
    gap: 20px;
  }
`

const HeaderSection = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`

const TitleGroup = styled.div`
  border-left: 4px solid #a6c10b;
  padding-left: 15px;
  h2 {
    color: ${(props) => props.theme.titleColor};
    font-size: 26px;
    letter-spacing: -0.5px;

    @media (max-width: 768px) {
      font-size: 20px;
    }
  }
  p {
    color: ${(props) => props.theme.subtitleColor};
    font-size: 13px;
    opacity: 0.9;
  }
`

const Badge = styled.span`
  background: rgba(166, 193, 11, 0.1);
  color: #a6c10b;
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 11px;
  border: 1px solid rgba(166, 193, 11, 0.3);
  font-weight: bold;

  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
  }
`

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 25px;

  @media (min-width: 1280px) {
    grid-template-columns: 1.8fr 1fr;
  }
`

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  flex: 1;
  height: 100%;
`

const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
`

const KpiCard = styled.div`
  background: ${(props) => props.theme.cardBackground};
  backdrop-filter: blur(10px);
  padding: 20px;
  border-radius: 16px;
  border: 1px solid ${(props) => props.theme.cardBorder};
  box-shadow: ${(props) => (props.theme.name === 'light' ? '0 4px 15px rgba(0, 31, 92, 0.06)' : 'none')};
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
  label {
    display: block;
    color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#8e9aaf')};
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    margin-bottom: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  strong {
    font-size: 22px;
    color: ${(props) => props.theme.textPrimary};
    display: block;

    @media (max-width: 768px) {
      font-size: 18px;
    }
  }
  .price {
    color: #a6c10b;
  }
`

const MapWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 720px;
  position: relative;

  @media (max-width: 768px) {
    min-height: 450px;
  }
`

// Estilo atualizado para receber o prop $isMinimized e alterar o layout
const FloatingDistributors = styled.div<{ $isMinimized: boolean }>`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: ${(props) => props.theme.background};
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: ${(props) => (props.$isMinimized ? '8px 12px' : '12px')};
  border-radius: 10px;
  width: 260px;
  z-index: 10;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  .header-dist {
    display: flex;
    justify-content: space-between;
    align-items: center;
    /* Remove as bordas e margens quando estiver minimizado */
    margin-bottom: ${(props) => (props.$isMinimized ? '0' : '10px')};
    border-bottom: ${(props) =>
      props.$isMinimized ? 'none' : '1px solid rgba(255, 255, 255, 0.05)'};
    padding-bottom: ${(props) => (props.$isMinimized ? '0' : '6px')};

    h4 {
      color: #a6c10b;
      font-size: 11px;
      margin: 0;
    }

    .actions {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .clear-btn {
      background: transparent;
      border: none;
      color: #ff4d4f;
      font-size: 10px;
      cursor: pointer;
      text-decoration: underline;
      padding: 0;

      &:hover {
        color: #ff7875;
      }
    }

    .toggle-btn {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: #fff;
      border-radius: 6px;
      width: 24px;
      height: 24px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(166, 193, 11, 0.2);
        color: #a6c10b;
        border-color: rgba(166, 193, 11, 0.4);
      }
    }
  }

  @media (max-width: 1024px) {
    display: none;
  }
`

const DistributorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const DistributorItem = styled.div<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  background: ${(props) => (props.$isSelected ? 'rgba(166, 193, 11, 0.15)' : 'transparent')};
  border: 1px solid ${(props) => (props.$isSelected ? 'rgba(166, 193, 11, 0.4)' : 'transparent')};

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  .rank {
    background: rgba(166, 193, 11, 0.15);
    color: ${(props) => (props.$isSelected ? '#fff' : '#a6c10b')};
    font-size: 10px;
    font-weight: bold;
    padding: 3px 5px;
    border-radius: 4px;
    min-width: 22px;
    text-align: center;
  }

  .info {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 100%;
  }

  .name {
    color: #fff;
    font-size: 11px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: ${(props) => (props.$isSelected ? 'bold' : 'normal')};
  }

  .volume {
    color: #8e9aaf;
    font-size: 10px;
  }
`

const GlassCard = styled.div`
  background: ${(props) => props.theme.cardBackground};
  backdrop-filter: blur(10px);
  padding: 25px;
  border-radius: 20px;
  border: 1px solid ${(props) => props.theme.cardBorder};
  box-shadow: ${(props) => props.theme.shadow};
  flex: 1;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 15px;
  }

  h3 {
    color: ${(props) => props.theme.textPrimary};
    font-size: 16px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .dot {
    width: 8px;
    height: 8px;
    background: #a6c10b;
    border-radius: 50%;
    box-shadow: 0 0 10px #a6c10b;
    transition: all 0.3s ease;
  }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`

const TabGroup = styled.div`
  display: flex;
  background: ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(0, 0, 0, 0.2)')};
  border-radius: 8px;
  padding: 4px;
`

const TabButton = styled.button<{ $active: boolean }>`
  background: ${(props) =>
    props.$active
      ? props.theme.name === 'light'
        ? '#ffffff'
        : 'rgba(255, 255, 255, 0.1)'
      : 'transparent'};
  color: ${(props) =>
    props.$active
      ? props.theme.name === 'light'
        ? '#0f172a'
        : '#fff'
      : props.theme.name === 'light'
      ? '#64748b'
      : '#8e9aaf'};
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: ${(props) => (props.$active ? '600' : 'normal')};
  box-shadow: ${(props) =>
    props.$active && props.theme.name === 'light' ? '0 2px 4px rgba(0,0,0,0.08)' : 'none'};

  &:hover {
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
  }
`
